/**
 * AlphaSense Internal Tools
 *
 * Shared core logic now lives in @carousel-office/agent-runtime.
 * This PPT wrapper keeps the existing tool contract and delegates
 * execution/response shaping to the shared implementation.
 */
import { executeAlphaSenseSearch, normalizeSearchAlphaSenseQueries, } from "../../../../../tools/searchAlphaSense";
import { ToolDescription } from "../../../../base/toolDescription";
import { ToolBase } from "../../../../base/toolBase";
import { generateContextID } from "../../../../../utils/idGenerator";
import { backendClient } from "../../../../../../services/communication/backendClient";
import { AlphaSenseSearchParamsSchema, } from "./search.schema";
// ═══════════════════════════════════════════════════════════════════════════
// ALPHASENSE SEARCH TOOL
// ═══════════════════════════════════════════════════════════════════════════
// System prompt fallback for Keywords AI
const ALPHASENSE_SEARCH_SYSTEM_PROMPT = `### searchAlphaSense
Search AlphaSense for financial research, earnings calls, SEC filings, and broker reports.

**When to use:**
- Searching for financial data, metrics, revenue, earnings
- Finding company information, competitor analysis
- Getting industry trends, market intelligence
- Researching specific companies or sectors

**BATCH QUERIES:** Send multiple queries in one call for parallel execution.
3 queries that take 90-180s sequentially complete in 30-60s in parallel.

**Query tips:**
- Be specific: "Apple Q4 2024 iPhone revenue" not "Apple revenue"
- Use use_findata_agent: true for numerical data (revenue, EPS, margins)
- Use use_findata_agent: false for qualitative insights (guidance, strategy)

\`\`\`json
{
  "queries": [
    { "query": "Microsoft Azure revenue Q4 2024", "use_findata_agent": true },
    { "query": "Microsoft AI strategy 2025", "use_findata_agent": false }
  ]
}
\`\`\`

Results include contextIds (as_xxx) for referencing in slides.`;
/**
 * Tool for searching AlphaSense research database.
 *
 * Supports BATCH queries for parallel execution - all queries in a single
 * call run concurrently on the Python backend, significantly reducing
 * total execution time.
 *
 * Example: 3 queries that would take 90-180 seconds sequentially
 * now complete in 30-60 seconds (parallel execution).
 */
export class AlphaSenseSearchTool extends ToolBase {
    constructor() {
        super();
        this.name = "searchAlphaSense";
        // System prompt description
        this.systemPromptDescription = ALPHASENSE_SEARCH_SYSTEM_PROMPT;
        // Streaming operation fields
        this.fieldsToCheck = [
            { name: 'uiDescription', type: 'string', list_status: false, order: 0 },
            { name: 'queries', type: 'array', list_status: true, order: 1 },
        ];
        this.listsToCheck = [
            {
                name: 'queries',
                type: 'array',
                item_type: 'object',
                list_status: true,
                order: 1,
                check_sub_field: 'query'
            },
        ];
        this.description = new ToolDescription("searchAlphaSense", `Search the AlphaSense research platform for financial data, company information, market analysis, and expert insights.

**BATCH QUERIES**: Submit MULTIPLE queries in one call - they execute in PARALLEL for 3x faster results!

AlphaSense aggregates research from:
- SEC filings and earnings transcripts
- Broker research and equity reports  
- News and trade publications
- Expert call transcripts
- Company presentations and investor decks

Best for questions about:
- Company financials and metrics (revenue, margins, growth)
- Market trends and competitive analysis
- Management commentary and strategic initiatives
- Industry dynamics and market sizing

**EFFICIENT USAGE** - Submit all queries at once:
{
  "queries": [
    { "query": "Apple Q4 2024 revenue and growth", "use_findata_agent": true },
    { "query": "Microsoft Azure cloud market share", "use_findata_agent": false },
    { "query": "Google AI investments and strategy", "use_findata_agent": false }
  ]
}

All queries run SIMULTANEOUSLY - 3 queries take the same time as 1 query!

Response includes parsed chunks with contextId field (e.g., as_7x9kf) for efficient referencing.`, AlphaSenseSearchParamsSchema);
    }
    async execute(parameters) {
        const queries = normalizeSearchAlphaSenseQueries(parameters);
        console.log(`[AlphaSenseSearchTool] Executing ${queries.length} queries in PARALLEL:`);
        for (const q of queries) {
            console.log(`  → "${q.query.slice(0, 60)}..." (findata: ${q.use_findata_agent})`);
        }
        try {
            const sharedResult = await executeAlphaSenseSearch(queries, {
                executeBatch: (batchQueries) => backendClient.alphaSenseSearchBatch(batchQueries),
                createContextId: () => generateContextID('as'),
            });
            const chunkIdMap = new Map(Object.entries(sharedResult.chunkIdMap));
            const result = {
                queryResults: sharedResult.queryResults,
                chunks: sharedResult.chunks,
                citations: sharedResult.citations,
                chunkIdMap,
                stats: sharedResult.stats,
            };
            console.log(`[AlphaSenseSearchTool] ✅ Batch complete: ${result.stats.successfulQueries}/${result.stats.totalQueries} queries, ${result.chunks.length} chunks total`);
            // Log per-query summary
            for (const qr of result.queryResults) {
                if (qr.error) {
                    console.log(`  ❌ "${qr.query.slice(0, 40)}..." - ERROR: ${qr.error}`);
                }
                else {
                    console.log(`  ✅ "${qr.query.slice(0, 40)}..." - ${qr.chunks.length} chunks`);
                }
            }
            return result;
        }
        catch (error) {
            console.error("[AlphaSenseSearchTool] ❌ Batch error:", error);
            throw error;
        }
    }
}
// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════
